import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useContext,
} from 'react'
import styled from 'styled-components'

import TextInput from 'components/Common/Form/Input/TextInput'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import InputText from 'components/Luxkit/Typography/InputText'
import { ModalElementContext } from 'contexts/ModalProvider'
import { UpdateTripError } from 'tripPlanner/api/types'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import { useUpdateTrip } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { BasicTrip, FullTrip } from 'tripPlanner/types/common'

interface Props {
  trip: BasicTrip | FullTrip
}

const CharCount = styled(InputText)`
  position: absolute;
  bottom: -4px;
  transform: translateY(100%);
  right: 0px;
`

const EditTripName = (props: Props) => {
  const { trip } = props
  const inputRef = useRef<HTMLInputElement>(null)

  const { closeModal, goBack } = useModalCallbacks()
  const modalElement = useContext(ModalElementContext)

  const onCloseModalClicked = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])
  const [name, setName] = useState(trip.name)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { mutate, isLoading } = useUpdateTrip({
    onSuccess: () => {
      onCloseModalClicked()
    },
    onError: (e: UpdateTripError) => {
      if (e.status === 422) {
        setErrorMsg(e.message)
      }
    },
  })

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.select()
    }
  }, [])

  const onUpdate = useCallback(() => {
    if (name.length) {
      mutate({
        tripId: trip.id,
        name,
      })
    }
  }, [mutate, name, trip.id])

  return (
    <>
      <ModalHeader
        title="Rename your trip"
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <ModalContent>
          <TextInput
            inputRef={inputRef}
            onChange={(e) => {
              setName(e.currentTarget.value)
              if (errorMsg) {
                setErrorMsg('')
              }
            }}
            manualError
            invalidErrorMessage={errorMsg}
            maxLength={MAX_TRIP_NAME_CHARS}
            data-testid="trip-popup-trip-input"
            value={name}
          >
            <CharCount variant="caption" colour="neutral-three">
              {name.length}/{MAX_TRIP_NAME_CHARS}
            </CharCount>
          </TextInput>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Save',
          disabled: isLoading || name.length == 0,
          onClick: onUpdate,
        }}
        secondaryActionProps={{
          children: 'Cancel',
          disabled: isLoading,
          onClick: onCloseModalClicked,
        }}
      />
    </>
  )
}

export default EditTripName
